/**
 * CountryTopicPage — one topic article inside one country.
 *
 * Per ARCHITECTURE §6.2 layout:
 *   1. Sticky progress bar (always on)
 *   2. Breadcrumb
 *   3. Hero (H1 + hook + defining number)
 *   4. Lead paragraph
 *   5. Body sections (filled from MDX / typed content later)
 *   6. Embedded widgets
 *   7. FAQ
 *   8. What to do next (CTA)
 *   9. Related topics + Compare-with-neighbours + Back-to-hub
 *  10. Last-verified footer
 *
 * The page is i18n + URL-prefix aware (see CountryHubPage for the pattern).
 * Mobile-first.  Sticky progress bar is 2 px on mobile, 4 px on desktop.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useJsonLd, useSeoMeta } from '@/hooks/useSeoMeta';
import { countriesQuery, type Country } from '@/data';
import { checklistPath } from '@/features/next-steps/slugs';
import {
  TOPICS,
  buildWikiUrl,
  getLangDef,
  isTopicSlug,
  slugToCountryCode,
  type CountryCode,
  type LangCode,
  type TopicSlug,
} from '@/features/wiki/architecture';

/** Curated topic relationships — 3 most-related per topic for the "Related chapters" grid. */
const RELATED_TOPIC_MAP: Record<TopicSlug, [TopicSlug, TopicSlug, TopicSlug]> = {
  'visa':             ['banking',        'taxes',          'cost-of-living'],
  'taxes':            ['property',       'banking',        'cost-of-living'],
  'cost-of-living':   ['property',       'transport',      'banking'],
  'property':         ['taxes',          'cost-of-living', 'banking'],
  'climate':          ['healthcare',     'safety-community', 'language-culture'],
  'healthcare':       ['education',      'safety-community', 'cost-of-living'],
  'education':        ['healthcare',     'language-culture', 'cost-of-living'],
  'banking':          ['taxes',          'visa',           'property'],
  'transport':        ['cost-of-living', 'infrastructure', 'climate'],
  'safety-community': ['healthcare',     'climate',        'language-culture'],
  'language-culture': ['education',      'safety-community', 'healthcare'],
  'infrastructure':   ['transport',      'climate',        'cost-of-living'],
};
import {
  buildTopicArticleJsonLd,
  useWikiHreflang,
} from '@/features/wiki/seo';
import { useTopicArticle } from '@/features/wiki/useArticle';
import { TopicBody, buildFaqsForJsonLd } from '@/features/wiki/Renderer';
import { ConsultantCallout } from '@/components/ConsultantCallout';
import { formatValue, useFactsForArticle } from '@/features/wiki/facts';
import type { TopicArticle } from '@/features/wiki/content-types';

/** Approximate word count across lead + all section paragraph/list/quote blocks. */
function countArticleWords(a: TopicArticle): number {
  const tokens = (s: string) =>
    s
      .replace(/<[^>]+>/g, ' ')
      .replace(/\{\{[^}]+\}\}/g, ' ')
      .replace(/\[\[[^\]]+\]\]/g, ' ')
      .split(/\s+/)
      .filter(Boolean).length;
  let n = tokens(a.lead);
  for (const sec of a.sections) {
    for (const b of sec.blocks) {
      if (b.kind === 'p' || b.kind === 'h3' || b.kind === 'note') n += tokens(b.text);
      else if (b.kind === 'list') n += b.items.reduce((sum, x) => sum + tokens(x), 0);
      else if (b.kind === 'quote') n += tokens(b.text);
    }
  }
  for (const f of a.faqs) n += tokens(f.question) + tokens(f.answer);
  return n;
}

interface CountryTopicPageProps {
  lang: LangCode;
}

export default function CountryTopicPage({ lang }: CountryTopicPageProps) {
  useDocumentLang();
  const { t, i18n } = useTranslation();
  const { slug, topic } = useParams<{ slug: string; topic: string }>();

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => {});
    }
  }, [lang, i18n]);

  // When the URL's country or topic changes (e.g. user clicks a Related
  // Topics tile at the bottom of one chapter), scroll back to the top
  // of the next chapter. Without this, react-router preserves the
  // previous chapter's scroll offset and the new chapter loads with
  // the user already past the hero.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slug, topic]);

  const cc = useMemo<CountryCode | null>(() => slugToCountryCode(slug), [slug]);
  const topicSlug: TopicSlug | null = isTopicSlug(topic) ? (topic as TopicSlug) : null;

  const { data: countriesFile, isLoading } = useQuery(countriesQuery());
  const country = useMemo<Country | null>(() => {
    if (!cc || !countriesFile) return null;
    return countriesFile.countries.find((c) => c.code === cc) ?? null;
  }, [cc, countriesFile]);

  const { article: topicArticle } = useTopicArticle(cc, topicSlug, lang);
  // Resolve facts once for the JSON-LD FAQ block so Google rich snippets carry
  // interpolated values (not literal {{fact.id}} tokens).
  const facts = useFactsForArticle(topicArticle?.plan.facts ?? [], cc, lang);
  const resolvedFaqs = topicArticle
    ? buildFaqsForJsonLd(topicArticle.faqs, facts, lang)
    : [];

  const countryName = country ? (lang === 'ru' ? country.name_ru : country.name_en) : '';
  const topicLabel = topicSlug
    ? t(`wiki.topic.${TOPICS.find((x) => x.slug === topicSlug)?.i18nKey ?? 'visa'}.label`)
    : '';

  // Sticky progress bar — single scroll listener, throttled via rAF.
  const progressRef = useRef<HTMLDivElement>(null);
  const [, setProgress] = useState(0);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      const next = total > 0 ? Math.min(1, Math.max(0, doc.scrollTop / total)) : 0;
      setProgress(next);
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${next})`;
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const articleHeadline = `${countryName} — ${topicLabel}`;
  // Article-specific description preferred over the country-level fallback —
  // PagePlan.seo.description is the canonical meta description.  Falls back to
  // the country fallback only when the article hasn't been loaded yet.
  const articleDescription =
    topicArticle?.plan.seo.description ??
    t('wiki.hub.metaDescription', { country: countryName });

  // SVG heroes get rewritten to .png for og:image — see CountryHubPage for
  // rationale and scripts/rasterize-og.mjs for the build step.
  const rawOg = topicArticle?.plan.seo.og_image ?? topicArticle?.hero?.src;
  const ogImagePath = rawOg && rawOg.endsWith('.svg') ? rawOg.replace(/\.svg$/, '.png') : rawOg;
  const ogImageAbs = ogImagePath
    ? `https://lodestar.place${ogImagePath}`
    : undefined;

  // Word count for Article JSON-LD — sum across lead + all section blocks.
  const wordCount = topicArticle
    ? countArticleWords(topicArticle)
    : undefined;

  useSeoMeta({
    title: topicArticle?.plan.seo.title
      ? `${topicArticle.plan.seo.title} · Lodestar`
      : `${articleHeadline} · Lodestar`,
    description: articleDescription,
    canonical: cc && topicSlug
      ? buildWikiUrl({ cc, topic: topicSlug, lang, origin: 'https://lodestar.place' })
      : undefined,
    locale: getLangDef(lang).ogLocale,
    ogType: 'article',
    ogImage: ogImageAbs,
    articlePublished: topicArticle?.plan.meta.updated,
    articleModified: topicArticle?.plan.meta.updated,
    articleSection: topicLabel,
    articleAuthor: topicArticle?.plan.meta.author,
    articleTags: topicArticle?.plan.intent.target_keywords,
  });

  useWikiHreflang({ cc: cc ?? undefined, topic: topicSlug ?? undefined });

  useJsonLd(
    cc && topicSlug && country
      ? buildTopicArticleJsonLd({
          cc,
          topic: topicSlug,
          lang,
          countryName,
          topicLabel,
          atlasLabel: t('wiki.atlas'),
          headline: articleHeadline,
          description: articleDescription,
          // No hardcoded fallback. This used to default both dates to
          // '2026-05-26', which asserted a specific modification date for any
          // article that had not set one, to Google, in Article schema. A
          // wrong date stated confidently is worse than an absent one: the
          // field is a ranking-relevant freshness signal, and the failure is
          // silent because the page renders fine either way.
          datePublished: topicArticle?.plan.meta.updated,
          dateModified: topicArticle?.plan.meta.updated,
          author: topicArticle?.plan.meta.author ?? 'Lodestar editors',
          imageUrl: ogImageAbs,
          wordCount,
          section: topicLabel,
          keywords: topicArticle?.plan.intent.target_keywords,
          faqs: resolvedFaqs.length > 0 ? resolvedFaqs : undefined,
        })
      : null,
    'wiki-topic-article',
  );

  if (!isLoading && (!cc || !topicSlug)) {
    return <TopicNotFound lang={lang} />;
  }

  if (isLoading || !country || !topicSlug) {
    return <TopicSkeleton />;
  }

  const hubUrl = buildWikiUrl({ cc: country.code as CountryCode, lang });
  const atlasUrl = buildWikiUrl({ lang });

  const currentIdx = TOPICS.findIndex((t) => t.slug === topicSlug);
  const prevTopic = currentIdx > 0 ? TOPICS[currentIdx - 1] : null;
  const nextTopic = currentIdx >= 0 && currentIdx < TOPICS.length - 1 ? TOPICS[currentIdx + 1] : null;

  const relatedSlugs = topicSlug ? RELATED_TOPIC_MAP[topicSlug] : null;
  const relatedTopics = relatedSlugs
    ? relatedSlugs.map((s) => TOPICS.find((t) => t.slug === s)!).filter(Boolean)
    : TOPICS.filter((x) => x.slug !== topicSlug).slice(0, 3);

  return (
    <>
      {/* Sticky progress bar */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 top-0 z-[1200] h-[2px] sm:h-[4px]"
      >
        <div
          ref={progressRef}
          className="h-full origin-left bg-terra-600 transition-transform duration-75 motion-reduce:transition-none"
          style={{ transform: 'scaleX(0)' }}
        />
      </div>

      <article className="mx-auto w-full max-w-3xl px-4 py-8 sm:py-12">
        {/* Breadcrumb */}
        <nav
          aria-label="breadcrumb"
          className="mb-5 font-mono text-[10px] uppercase tracking-wider text-ink-400"
        >
          <Link to={atlasUrl} className="hover:text-ink-700">
            {t('wiki.atlas')}
          </Link>
          <span aria-hidden="true" className="mx-1.5">/</span>
          <Link to={hubUrl} className="hover:text-ink-700">
            {countryName}
          </Link>
          <span aria-hidden="true" className="mx-1.5">/</span>
          <span className="text-ink-700">{topicLabel}</span>
        </nav>

        {/* Hero — above the mobile fold */}
        <header className="mb-8 sm:mb-10">
          <p className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
            <span className="mr-1.5">{country.flag_emoji}</span>
            {countryName} · {topicLabel}
          </p>
          <h1 className="mt-2 font-display text-[clamp(28px,6vw,40px)] font-medium italic tracking-tight text-ink-900">
            {articleHeadline}
          </h1>
          <p className="mt-3 text-base text-ink-700 sm:text-lg">
            {articleDescription}
          </p>
        </header>

        {/* Body — real content when a TopicArticle exists, else draft placeholder.
            Real content includes lead + sections + FAQ inline (TopicBody) — the
            FAQ skeleton section below is suppressed in that case. */}
        {topicArticle ? (
          <TopicBody article={topicArticle} cc={country.code as CountryCode} lang={lang} />
        ) : (
          <>
            <section
              aria-labelledby="article-body-title"
              className="prose prose-lg max-w-none rounded-lg border border-dashed border-ink-100 bg-paper-2/60 p-5 text-sm text-ink-500"
            >
              <p className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                {t('wiki.hub.draftBadge')}
              </p>
              <p className="mt-2">{t('wiki.hub.comingSoonContent')}</p>
            </section>

            <section aria-labelledby="faq-title" className="mt-12">
              <h2
                id="faq-title"
                className="font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
              >
                {t('wiki.article.faqTitle')}
              </h2>
              <div className="mt-4 rounded-lg border border-dashed border-ink-100 bg-paper-2/60 p-5 text-sm text-ink-500">
                {t('wiki.hub.comingSoonContent')}
              </div>
            </section>
          </>
        )}

        {/* CTA — what to do next */}
        <section className="mt-12 border-t border-ink-100 pt-6">
          <div className="flex flex-wrap gap-3">
            <Link
              to={checklistPath(country.code)}
              className="inline-flex h-11 items-center rounded-md bg-terra-600 px-5 text-sm font-medium text-paper transition-colors hover:bg-terra-700"
            >
              {t('wiki.hub.ctaChecklist')}
            </Link>
            <Link
              to={hubUrl}
              className="inline-flex h-11 items-center rounded-md border border-ink-100 px-5 text-sm font-medium text-ink-700 transition-colors hover:bg-paper-2"
            >
              {t('wiki.article.backToHub', { country: countryName })}
            </Link>
          </div>
        </section>

        {/* Specialist CTA — context-sensitive per topic */}
        <ConsultantCallout
          source={`topic_${topicSlug}`}
          variant="card"
          className="mt-10"
          country={country.code}
          specialty={topicSlug}
        />

        {/* Related topics */}
        <section aria-labelledby="related-title" className="mt-14">
          <h2
            id="related-title"
            className="font-display text-lg font-medium italic tracking-tight text-ink-900 sm:text-xl"
          >
            {t('wiki.article.relatedTopics')}
          </h2>
          <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {relatedTopics.map((rt) => (
              <li key={rt.slug} className="flex">
                <Link
                  to={buildWikiUrl({
                    cc: country.code as CountryCode,
                    topic: rt.slug,
                    lang,
                  })}
                  className="flex h-full w-full items-center gap-3 rounded-md border border-ink-100 bg-paper-2/60 px-4 py-3 transition-colors hover:bg-paper-2"
                >
                  <span aria-hidden="true" className="text-2xl leading-none">
                    {rt.icon}
                  </span>
                  <span>
                    <span className="block font-display text-base font-medium italic text-ink-900">
                      {t(`wiki.topic.${rt.i18nKey}.label`)}
                    </span>
                    <span className="block text-xs text-ink-500">
                      {t(`wiki.topic.${rt.i18nKey}.description`)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* Prev / next chapter navigation */}
        {(prevTopic || nextTopic) && (
          <nav
            aria-label="chapter navigation"
            className="mt-10 grid grid-cols-2 gap-3 border-t border-ink-100 pt-6"
          >
            <div>
              {prevTopic && (
                <Link
                  to={buildWikiUrl({ cc: country.code as CountryCode, topic: prevTopic.slug, lang })}
                  className="group flex flex-col rounded-md border border-ink-100 bg-paper-2/60 px-4 py-3 transition-colors hover:bg-paper-2"
                >
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                    ← {t('wiki.article.prevChapter')}
                  </span>
                  <span className="mt-1 font-display text-sm font-medium italic text-ink-900 group-hover:text-terra-700">
                    {prevTopic.icon} {t(`wiki.topic.${prevTopic.i18nKey}.label`)}
                  </span>
                </Link>
              )}
            </div>
            <div className="flex justify-end">
              {nextTopic && (
                <Link
                  to={buildWikiUrl({ cc: country.code as CountryCode, topic: nextTopic.slug, lang })}
                  className="group flex flex-col rounded-md border border-ink-100 bg-paper-2/60 px-4 py-3 text-right transition-colors hover:bg-paper-2"
                >
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                    {t('wiki.article.nextChapter')} →
                  </span>
                  <span className="mt-1 font-display text-sm font-medium italic text-ink-900 group-hover:text-terra-700">
                    {nextTopic.icon} {t(`wiki.topic.${nextTopic.i18nKey}.label`)}
                  </span>
                </Link>
              )}
            </div>
          </nav>
        )}

        {/* All chapters link */}
        <div className="mt-4 text-center">
          <Link
            to={hubUrl}
            className="font-mono text-[11px] uppercase tracking-wider text-ink-400 hover:text-ink-900"
          >
            {t('wiki.article.allTopics')} ↑
          </Link>
        </div>

        {/* Last-verified footer. The date is plan.meta.updated, the same value the
            page's JSON-LD sends as dateModified; article-updated-date.test.ts keeps
            it no older than the newest lastVerified among the article's facts. It
            used to print a hard-coded dash on every published chapter. */}
        {topicArticle?.plan.meta.updated && (
          <footer className="mt-10 border-t border-ink-100 pt-4 text-xs text-ink-400">
            {t('wiki.article.verifiedAt', {
              date: formatValue(topicArticle.plan.meta.updated, 'date_full', lang),
            })}
          </footer>
        )}
      </article>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Skeletons                                                          */
/* ────────────────────────────────────────────────────────────────── */

function TopicSkeleton() {
  return (
    <div aria-hidden="true" className="mx-auto w-full max-w-3xl animate-pulse px-4 py-12">
      <div className="mb-4 h-3 w-32 rounded bg-ink-100" />
      <div className="mb-3 h-10 w-3/4 rounded bg-ink-100" />
      <div className="mb-8 h-4 w-2/3 rounded bg-ink-100" />
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-4 w-full rounded bg-ink-100" />
        ))}
      </div>
    </div>
  );
}

function TopicNotFound({ lang }: { lang: LangCode }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-medium italic tracking-tight text-ink-900">
        {t('wiki.notFound.title')}
      </h1>
      <p className="mt-3 text-sm text-ink-500">{t('wiki.notFound.subtitle')}</p>
      <Link
        to={buildWikiUrl({ lang })}
        className="mt-6 inline-flex h-11 items-center rounded-md bg-terra-600 px-5 text-sm font-medium text-paper hover:bg-terra-700"
      >
        {t('wiki.notFound.ctaAtlas')}
      </Link>
    </div>
  );
}
